// src/services/mediaService.js

import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy, 
  limit,
  increment,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../config/firebase';

export class MediaService {
  static detectMediaType(url) {
    if (!url) return null;
    
    const cleanUrl = url.toLowerCase().trim();
    
    // YouTube
    if (cleanUrl.includes('youtube.com') || cleanUrl.includes('youtu.be')) {
      return 'youtube';
    }
    
    // PDF
    if (cleanUrl.includes('.pdf') || cleanUrl.includes('pdf')) {
      return 'pdf';
    }
    
    // Images
    if (cleanUrl.match(/\.(jpg|jpeg|png|gif|webp|svg|bmp|ico)(\?|$)/)) {
      return 'image';
    }
    
    // Videos
    if (cleanUrl.match(/\.(mp4|webm|ogg|avi|mov|wmv|flv|mkv)(\?|$)/)) {
      return 'video';
    }
    
    // Audio
    if (cleanUrl.match(/\.(mp3|wav|ogg|aac|flac|m4a)(\?|$)/)) {
      return 'audio';
    }
    
    // Google Drive
    if (cleanUrl.includes('drive.google.com')) {
      return 'google-drive';
    }
    
    // Vimeo
    if (cleanUrl.includes('vimeo.com')) {
      return 'vimeo';
    }
    
    // Default to web embed
    return 'embed';
  }
  
  static getYouTubeVideoId(url) {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/watch\?.*v=([^&\n?#]+)/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    
    return null;
  }
  
  static getVimeoVideoId(url) {
    const match = url.match(/vimeo\.com\/(?:.*\/)?(\d+)/);
    return match ? match[1] : null;
  }
  
  static convertGoogleDriveUrl(url) {
    // Convert Google Drive share URLs to direct view URLs
    const fileIdMatch = url.match(/\/file\/d\/([a-zA-Z0-9-_]+)/);
    if (fileIdMatch) {
      return `https://drive.google.com/file/d/${fileIdMatch[1]}/preview`;
    }
    return url;
  }
  
  static isValidUrl(string) {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  }
  
  static sanitizeUrl(url) {
    if (!url) return '';
    
    // Remove dangerous protocols
    if (url.match(/^(javascript|data|vbscript):/i)) {
      return '';
    }
    
    // Ensure proper protocol
    if (!url.match(/^https?:\/\//i)) {
      return `https://${url}`;
    }
    
    return url;
  }

  static async uploadMultipleFiles(files, path, onProgress) {
    const uploadPromises = files.map(async (file, index) => {
      try {
        const timestamp = Date.now();
        const fileName = `${timestamp}_${index}_${file.name}`;
        const storageRef = ref(storage, `${path}/${fileName}`);
        
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        
        if (onProgress) {
          onProgress(index + 1, files.length);
        }
        
        return {
          url: downloadURL,
          fileName: file.name,
          fileType: file.type,
          index
        };
      } catch (error) {
        console.error(`Error uploading file ${file.name}:`, error);
        return { error: error.message, fileName: file.name, index };
      }
    });

    return await Promise.all(uploadPromises);
  }
  
  static getMediaMetadata(url, type) {
    const metadata = {
      url,
      type,
      title: '',
      description: '',
      thumbnail: '',
      duration: null,
      size: null
    };
    
    switch (type) {
      case 'youtube':
        const videoId = this.getYouTubeVideoId(url);
        if (videoId) {
          metadata.thumbnail = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
          metadata.title = 'YouTube Video';
        }
        break;
        
      case 'vimeo':
        const vimeoId = this.getVimeoVideoId(url);
        if (vimeoId) {
          metadata.title = 'Vimeo Video';
        }
        break;
        
      case 'pdf':
        metadata.title = 'PDF Document';
        metadata.description = 'Portable Document Format';
        break;
        
      case 'image':
        metadata.title = 'Image';
        metadata.thumbnail = url;
        break;
        
      default:
        metadata.title = 'Web Content';
    }
    
    return metadata;
  }
  
}